import type { PriceTier, ServerPlan } from '@/types/pricing';

/**
 * Per-seat volume pricing for the Firms & Teams tab.
 *
 * Firms buy the SAME individual plans as everyone else, with a seat count and
 * a volume discount — there is no separate org plan ladder and no admin fee
 * (the `starter_org` / `plus_org` SKUs were retired in
 * `20260908045256_publish_firm_seat_pricing.sql`).
 *
 * The ladder itself is DB-owned, in `public.plans.price_tiers` (JSONB):
 * ascending `up_to` bands, last entry open-ended (`up_to: null`). Nothing in
 * this module hardcodes a price, a band boundary, a discount percentage or the
 * contact-sales threshold — every one of them is derived from the tiers the
 * catalog hands over, so a repricing migration is picked up with no code
 * change. That is deliberate: the discount copy this replaces was hardcoded in
 * the message catalog ("10+ miest: 11-17% zľava") and was already factually
 * wrong against the prices it described.
 *
 * All amounts here are SK-gross EUR, matching `plans.price_tiers`. The VAT view
 * is applied afterwards by `vatAdjustedDisplayPrice` at the call site.
 */

/**
 * The band covering `seats`: the first band whose `up_to` is `null`
 * (open-ended) or at least `seats`.
 *
 * Bands are read in array order rather than sorted — `up_to` is documented
 * ascending and a mis-ordered array is a catalog defect that should surface,
 * not be silently repaired here. Returns `null` for an empty/absent ladder or
 * a non-positive seat count.
 */
export function resolveSeatBand(
  tiers: readonly PriceTier[] | null | undefined,
  seats: number
): PriceTier | null {
  if (!tiers || tiers.length === 0 || !Number.isFinite(seats) || seats < 1) {
    return null;
  }
  return (
    tiers.find((tier) => tier.up_to === null || seats <= tier.up_to) ?? null
  );
}

/**
 * Per-seat gross EUR for `seats` on `plan`.
 *
 * Degrades to the plan's flat individual price (`plan.price`, in cents) when
 * the catalog carries no `price_tiers` — the cold-load state before the
 * pricing migration is applied, where the honest answer is "the list price",
 * not `NaN`. Returns `null` only when neither source has a usable number.
 */
export function seatUnitPriceEur(
  plan: Pick<ServerPlan, 'price' | 'price_tiers'> | undefined,
  seats: number
): number | null {
  if (!plan) return null;
  const band = resolveSeatBand(plan.price_tiers, seats);
  if (band) return band.unit_amount_eur;
  return plan.price > 0 ? plan.price / 100 : null;
}

/**
 * Discount percentage of `seats`' band against the ladder's FIRST band, which
 * is by construction the 1-seat (list, no discount) price.
 *
 * Rounded to a whole percent for display. Returns `0` on the first band and
 * `null` when there is no ladder to compare against.
 */
export function seatDiscountPct(
  tiers: readonly PriceTier[] | null | undefined,
  seats: number
): number | null {
  if (!tiers || tiers.length === 0) return null;
  const baseTier = tiers[0];
  const band = resolveSeatBand(tiers, seats);
  if (!baseTier || !band || baseTier.unit_amount_eur <= 0) return null;
  return Math.round(
    ((baseTier.unit_amount_eur - band.unit_amount_eur) /
      baseTier.unit_amount_eur) *
      100
  );
}

/**
 * Smallest seat count that falls into the ladder's open-ended band — i.e. the
 * point at which the per-seat price stops being published and the firm is sent
 * to sales instead.
 *
 * The last band is stored so a negotiated deal has a documented floor, but it
 * is deliberately never shown; this is what tells the UI where to stop. Derived
 * from the second-to-last band's `up_to`, so a re-banding migration moves the
 * threshold automatically.
 *
 * Returns `null` when there is nothing withheld to hide behind: a ladder with
 * fewer than two bands, or one whose last band is not open-ended.
 */
export function seatContactThreshold(
  tiers: readonly PriceTier[] | null | undefined
): number | null {
  if (!tiers || tiers.length < 2) return null;
  const last = tiers[tiers.length - 1];
  const penultimate = tiers[tiers.length - 2];
  if (!last || last.up_to !== null) return null;
  if (!penultimate || penultimate.up_to === null) return null;
  return penultimate.up_to + 1;
}

/** One published (i.e. price-bearing) row of the volume-discount summary. */
interface SeatDiscountBand {
  /** First seat count in the band. */
  from: number;
  /**
   * Last seat count in the band — always a real number.
   *
   * The open-ended band is never published (the loop below stops at it), so
   * this is deliberately NOT `number | null`: a nullable type here forced the
   * caller into a `?? 0` fallback that could only ever render a nonsense
   * "5-0 seats" range if the invariant moved. Narrow type, no dead branch.
   */
  to: number;
  /** Lowest whole-percent discount across the plans compared, vs their band 1. */
  minPct: number;
  /** Highest whole-percent discount across the plans compared. */
  maxPct: number;
}

/**
 * The volume-discount summary shown once for the whole section, derived across
 * every plan's ladder rather than stated as prose.
 *
 * Band boundaries are taken from the first plan that has a ladder; the
 * percentages are the min/max across every plan sharing that boundary, because
 * the three tiers do not discount by exactly the same amount at the same band
 * (e.g. the 10-24 band is 16 % / 14 % / 15 %, which reads as "14-16 %").
 *
 * The first band (list price, 0 %) and the open-ended band (deliberately
 * unpublished) are both excluded — callers render the open-ended band as a
 * contact-sales row using {@link seatContactThreshold}.
 */
export function seatDiscountBands(
  plans: readonly (Pick<ServerPlan, 'price_tiers'> | undefined)[]
): SeatDiscountBand[] {
  const ladders = plans
    .map((plan) => plan?.price_tiers)
    .filter((tiers): tiers is PriceTier[] => !!tiers && tiers.length > 0);

  const reference = ladders[0];
  if (!reference) return [];

  const bands: SeatDiscountBand[] = [];
  for (let i = 1; i < reference.length; i += 1) {
    const tier = reference[i];
    const previous = reference[i - 1];
    // `previous.up_to === null` would mean an open-ended band sits before the
    // last one — a malformed ladder. Stop rather than invent a `from`.
    if (!tier || !previous || previous.up_to === null) break;
    // The open-ended band is stored but never published.
    if (tier.up_to === null) break;

    // Hoisted so the null-narrowing survives into the closure below.
    const from = previous.up_to + 1;
    const percentages = ladders
      .map((tiers) => seatDiscountPct(tiers, from))
      .filter((pct): pct is number => pct !== null);
    if (percentages.length === 0) continue;

    bands.push({
      from,
      to: tier.up_to,
      minPct: Math.min(...percentages),
      maxPct: Math.max(...percentages),
    });
  }
  return bands;
}

/**
 * Whether a tier's price must be WITHHELD for this seat count, i.e. the card
 * shows the contact CTA instead of a number.
 *
 * Three independent reasons, and the third is the one that is easy to miss:
 *
 *   1. `isContactSales` — the section-level rule, seats at or above the
 *      ladder's open-ended band. Passed in so it has exactly one owner.
 *   2. `hasUnitPrice === false` — nothing usable to price from at all.
 *   3. **No ladder, more than one seat.** Until the seat-pricing migration is
 *      APPLIED, every individual tier carries `price_tiers = NULL` in prod, so
 *      `seatContactThreshold` is `null`, `isContactSales` can never become
 *      true, and the card would happily publish `list price x seats` with no
 *      volume discount and no upper bound — measured in a browser at 500 seats
 *      as "Spolu 49500.00 EUR/mes". That is a quote we cannot honour: with no
 *      ladder there is no defined multi-seat price. One seat is still the
 *      honest list price, so only `seats > 1` is withheld.
 *
 * This closes the window between DEPLOYING this frontend and APPLYING the
 * migration, during which the two are legitimately out of step.
 */
export function shouldWithholdSeatPrice(args: {
  hasLadder: boolean;
  seats: number;
  isContactSales: boolean;
  hasUnitPrice: boolean;
}): boolean {
  const { hasLadder, seats, isContactSales, hasUnitPrice } = args;
  if (!hasUnitPrice) return true;
  if (isContactSales) return true;
  return !hasLadder && seats > 1;
}
